import React, { useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientSearchResultType } from '@atlassian/search-client/common/types';
import { getGenericUrlWithAnalyticsQueryParams } from '@atlassian/search-common/analytics-query-params';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';

import {
	ProductKeys,
	ProductKeys1PForAnalytics,
} from '../../../../../../common/constants/products';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { getAtlasEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { ProductIcon } from '../../../../../../common/ui/product-icon';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type {
	searchAtlasGoal_AtlassianSearchPage$data,
	searchAtlasGoal_AtlassianSearchPage$key,
} from './__generated__/searchAtlasGoal_AtlassianSearchPage.graphql';
import { GoalTitleMetadata } from './goal-title-metadata';

export const SearchAtlasGoal = ({
	node,
	position,
	searchQuery,
	searchSessionId,
	primaryProduct,
	primarySubProduct,
	selectedProduct,
	appMode,
	mainContentWidth,
}: SearchResultProps<searchAtlasGoal_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchAtlasGoal_AtlassianSearchPage on SearchResultAtlasGoal
			@argumentDefinitions(includeIconUrl: { type: "Boolean", defaultValue: false }) {
				...goalTitleMetadata_AtlassianSearchPage
				__typename
				id
				title
				description
				type
				url
				iconUrl @include(if: $includeIconUrl)
				goal {
					key
					description
				}
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type]);
	const onResultClicked = useFireResultClicked({
		metadata,
		position,
	});
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
	});
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const url = useMemo(() => {
		return getGenericUrlWithAnalyticsQueryParams({ url: data.url, searchSessionId });
	}, [data.url, searchSessionId]);

	const headerMetadataParts = [
		['type', <SearchResultType type={data.type as SearchClientSearchResultType} />],
	];

	const enrichedTitle = useMemo(() => {
		const prefix = data.goal?.key;

		if (prefix) {
			return enrichExcerpt(prefix.concat('\u2002', data.title));
		}
		return enrichExcerpt(data.title);
	}, [data.title, data.goal?.key]);

	const description = getDescription(data);
	const excerptDescription = data.description;
	const enrichedDescription = useMemo(() => {
		if (excerptDescription) {
			return enrichExcerpt(excerptDescription);
		}

		const { highlightedTitle } = highlightMatchedText(description, searchQuery.split(' '));
		if (!highlightedTitle) {
			return description;
		}
		return processExcerpt(highlightedTitle);
	}, [description, excerptDescription, searchQuery]);

	let icon: React.JSX.Element;
	if (fg('rovo_search_confluence_icons_fps')) {
		icon = getAtlasEntityIcon({
			iconUrl: data.iconUrl,
			type: data.type,
			size: 'medium',
		});
	} else {
		icon = <ProductIcon product={ProductKeys.Atlas} label="Atlas" />;
	}

	return (
		<SearchResult
			{...data}
			product={
				fg('rovo_search_cross_product_url_wrapping')
					? ProductKeys1PForAnalytics.AtlasGoals
					: ProductKeys.Atlas
			}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			icon={icon}
			headerMetadataParts={headerMetadataParts}
			titleMetadata={<GoalTitleMetadata metadata={data} />}
			title={enrichedTitle}
			description={
				<Box xcss={descriptionContainerStyles} testId="search-page-atlas-result-description">
					<DescriptionBody description={enrichedDescription} />
				</Box>
			}
			position={position}
			partial={data}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
		/>
	);
};

export const isSearchResultAtlasGoal = (
	node?: any,
): node is searchAtlasGoal_AtlassianSearchPage$key => {
	return node?.id?.includes('townsquare') && node?.__typename === 'SearchResultAtlasGoal';
};

const descriptionContainerStyles = xcss({
	overflow: 'hidden',
	maxHeight: '60px',
});

type GoalDescription = {
	type: string;
	content?: GoalDescription[];
	text?: string;
};

const getDescription = (data: searchAtlasGoal_AtlassianSearchPage$data) => {
	let description = '';
	if (!data.goal?.description) {
		return '';
	}

	const parsedDescrption: GoalDescription = JSON.parse(data.goal.description);

	// Recursive function to build the description. Traversal occurs in DFS
	// order. The base case is when the node is a text node, in which case
	// the text is appended to the description.
	const buildDescription = (goalDescription: GoalDescription) => {
		if (goalDescription.content) {
			goalDescription.content.forEach(buildDescription);
		} else if (goalDescription.type === 'text') {
			description += goalDescription.text;
		}
	};

	if (parsedDescrption.content) {
		buildDescription(parsedDescrption);
	}

	return description;
};
